import React, { useState } from 'react'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'
import SpektrixFetcher from '@lib/spektrix/fetcher'
import { addMembershipToBasket } from '@lib/spektrix'
import BasketUpdateEvent from '@lib/basket-update-event'
import {
  ArrowPathIcon,
  CheckIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline'

const Memberships = () => {
  const [addingToBasket, setAddingToBasket] = useState({})
  const [successMessages, setSuccessMessages] = useState({})

  // Fetch memberships data
  const { data, error, isLoading } = useSWR(
    '/memberships.json',
    fetcher
  )

  // Fetch current basket to check if user is logged in
  const { data: basket } = useSWR('/basket', SpektrixFetcher)

  const handleAddToBasket = async (membershipId) => {
    try {
      setAddingToBasket(prev => ({ ...prev, [membershipId]: true }))
      
      // Add membership to basket
      await addMembershipToBasket(membershipId)
      
      // Show success message
      setSuccessMessages(prev => ({ ...prev, [membershipId]: true }))
      
      // Dispatch basket update event
      window.dispatchEvent(new Event(BasketUpdateEvent))
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessages(prev => {
          const updated = { ...prev }
          delete updated[membershipId]
          return updated
        })
      }, 3000)
    } catch (err) {
      console.error('Failed to add membership to basket:', err)
      alert('Failed to add membership to basket. Please try again.')
    } finally {
      setAddingToBasket(prev => {
        const updated = { ...prev }
        delete updated[membershipId]
        return updated
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <ArrowPathIcon className="w-8 h-8 text-gray-500 dark:text-gray-400 animate-spin mr-2" />
        <span className="text-gray-700 dark:text-gray-300">Loading memberships...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-600 dark:text-red-400">
          Failed to load memberships. Please try again later.
        </p>
      </div>
    )
  }

  const memberships = data?.memberships || []

  if (memberships.length === 0) {
    return (
      <div className="text-center py-16">
        <UserGroupIcon className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">
          No memberships available at the moment.
        </p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {memberships.map(membership => (
        <div
          key={membership.id}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 h-auto md:h-[600px] flex flex-col"
        >
          {/* Call to Action Section - Fixed at top */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white h-14 flex items-start flex-1 pr-3">
                {membership.name}
              </h3>
              <div className="bg-gray-800 dark:bg-gray-700 text-white px-3 py-1 rounded-lg text-lg font-bold flex-shrink-0">
                {membership.price === 0 ? 'FREE' : `£${Math.round(membership.price)}`}
              </div>
            </div>
            
            {membership.isOnSale ? (
              <button
                onClick={() => handleAddToBasket(membership.id)}
                disabled={addingToBasket[membership.id]}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                  successMessages[membership.id]
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {addingToBasket[membership.id] ? (
                  <span className="flex items-center justify-center">
                    <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                    Adding...
                  </span>
                ) : successMessages[membership.id] ? (
                  <span className="flex items-center justify-center">
                    <CheckIcon className="w-5 h-5 mr-2" />
                    Added to basket
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <ShoppingBagIcon className="w-5 h-5 mr-2" />
                    Add to basket
                  </span>
                )}
              </button>
            ) : (
              <div className="text-center py-3 px-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <span className="text-gray-500 dark:text-gray-400">
                  Not currently available
                </span>
              </div>
            )}
            
            {membership.validityText && (
              <div className="flex items-center justify-center text-sm text-gray-600 dark:text-gray-400 mt-3">
                <CalendarDaysIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                <span>Valid for {membership.validityText}</span>
              </div>
            )}
          </div>

          {/* Scrollable Content Section */}
          <div className="flex-1 md:overflow-y-auto">
            {membership.imageUrl && (
              <div className="aspect-video bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                <img
                  src={membership.imageUrl}
                  alt={membership.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <div className="px-6 pb-6">
            {membership.htmlDescription && membership.htmlDescription.trim() && (
              <div
                className="text-gray-600 dark:text-gray-400 prose prose-sm max-w-none mb-4 pt-6 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_p:first-child]:mt-0 [&_div]:space-y-0 [&_div]:mt-0 [&>*:first-child]:mt-0"
                dangerouslySetInnerHTML={{ __html: membership.htmlDescription }}
              />
            )}              
              <div className={`space-y-3 ${!membership.htmlDescription || !membership.htmlDescription.trim() ? 'pt-6' : ''}`}>
                {membership.benefits && membership.benefits.length > 0 && (
                  <div className="space-y-1">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Benefits:</h4>
                    {membership.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                        <CheckIcon className="w-4 h-4 mr-2 flex-shrink-0 text-green-500 mt-0.5" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default Memberships